/**
 * Platform placement for elevation sections (ADR 0040 — garage systems).
 *
 * A sketch can stand its cabinetry on a PLATFORM (garage systems typically use
 * a 3" or 6" base). Pure geometry, floor-up, in the room's unit of measure.
 *
 * How a section responds to the platform depends on its TYPE ANCHOR — the shop
 * builds three ways, so the platform is NOT a uniform shift:
 *
 *   - 'floor'      (default; hanging / tall / every CURRENT section type): the
 *     section RIDES UP — its bottom rises to the platform top and its height is
 *     unchanged, so its top rises by the platform height (an 84" panel on a 6"
 *     platform tops out at 90"). Stands on legs.
 *   - 'countertop' (base cabinets): the work SURFACE height is fixed (a 34 1/2"
 *     base keeps its 34 1/2" countertop), so the cabinet SHRINKS — bottom rises
 *     to the platform, top stays put, height becomes (workSurface − platform),
 *     e.g. 34 1/2" → 31 1/2" on a 3" platform. Stands on legs.
 *   - 'top'        (upper cabinets): the top ALIGNS with the tall-cabinet tops
 *     (which rose by the platform), so the whole upper rises by the platform.
 *     Wall-hung — no legs.
 *
 * NOTE base and upper are not section TYPES yet (2026-07-28) — only 'floor' is
 * exercised live. The other two are implemented + unit-tested here so the data
 * model and section-type maintenance are ready when those types are added
 * (owner: "the tables and maintenance need to take this into account").
 */

/** The anchor values a section type may declare; 'floor' is the default. */
export const SECTION_ANCHORS = ['floor', 'countertop', 'top'];

/**
 * @param {object} section { heightUnits, anchor?, workSurfaceUnits? }
 * @param {object} ctx { platformUnits, tallTopUnits? } — tallTopUnits is the
 *   pre-platform top of the tallest FLOOR-anchored section, for 'top' alignment.
 * @returns {{ bottomUnits:number, topUnits:number, hasLegs:boolean }}
 *   floor-up extents of the drawn section body, and whether it stands on legs.
 */
export function placeSectionOnPlatform(section = {}, ctx = {}) {
  const P = ctx.platformUnits > 0 ? ctx.platformUnits : 0;
  const H = section.heightUnits > 0 ? section.heightUnits : 0;
  const anchor = SECTION_ANCHORS.includes(section.anchor) ? section.anchor : 'floor';

  // 'top' (uppers) is wall-hung and top-referenced ALWAYS — an upper hangs so
  // its top matches the standard tall panel (tallTopUnits, 95.25") whether or
  // not there's a platform; on a platform the reference rises with the talls.
  // Its mounting "elevation" is therefore derived (top − height), which is what
  // the owner asked for: uppers have no fixed default elevation.
  if (anchor === 'top') {
    const top = (ctx.tallTopUnits > 0 ? ctx.tallTopUnits : H) + P;
    return { bottomUnits: Math.max(top - H, 0), topUnits: top, hasLegs: false };
  }

  // Floor / countertop with no platform: sit on the floor, unchanged.
  if (P <= 0) return { bottomUnits: 0, topUnits: H, hasLegs: false };

  if (anchor === 'countertop') {
    // Fixed work surface: the top stays, the body shrinks off the bottom.
    const surface = section.workSurfaceUnits > 0 ? section.workSurfaceUnits : H;
    return { bottomUnits: P, topUnits: Math.max(surface, P), hasLegs: true };
  }

  // 'floor': ride up by the platform, height unchanged.
  return { bottomUnits: P, topUnits: P + H, hasLegs: true };
}

/**
 * Whether an elevation reference-line KIND rides UP with the sections on a
 * platform. Panel tops and the bottom-shelf datum are section features, so they
 * rise; a countertop/desk work surface and a stacked-unit reach are true
 * heights above the floor, so they stay put.
 */
export function referenceLineRidesPlatform(kind) {
  return kind === 'panel_height' || kind === 'base_shelf';
}
